import { useRef, useEffect, useMemo } from 'react'
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  ToolboxComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import Box from '@mui/material/Box'
import { useTheme, alpha } from '@mui/material/styles'
import { PERSONAS } from './cohorts'
import { cumulativeSeries, fmtHours } from './micTime'
import { CHART_GROUP } from './Timeline'

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  ToolboxComponent,
  CanvasRenderer,
])

const HEIGHT = 260

// Series render in stack order; the tooltip lists them in legend order.
const LEGEND_RANK = new Map(PERSONAS.map(({ key }, index) => [key, index]))

const tooltipFormatter = (params) => {
  const rows = [...params]
    .sort(
      (a, b) => LEGEND_RANK.get(a.seriesName) - LEGEND_RANK.get(b.seriesName)
    )
    .map(
      ({ marker, seriesName, value }) =>
        `<div style="display:flex;justify-content:space-between;gap:18px;">
          <span>${marker}${seriesName}</span><b>≈ ${fmtHours(value[1])}</b>
        </div>`
    )
    .join('')
  const total = params.reduce((sum, { value }) => sum + value[1], 0)
  return `<div style="min-width:160px">
    <div style="margin-bottom:4px;">${params[0].axisValueLabel}</div>
    ${rows}
    <div style="display:flex;justify-content:space-between;gap:18px;
      border-top:1px solid rgba(255,255,255,0.25);margin-top:4px;padding-top:4px;">
      <span>Total</span><b>≈ ${fmtHours(total)}</b>
    </div>
  </div>`
}

// The detailed companion to the bucket: cumulative mic-on user-hours as
// a persona-stacked area over whatever window the shared range picker
// (and drag-zoom) selects. Legend clicks focus one group — off the
// stack, so it shows that group's own accumulation from zero.
const CumulativeTimeline = ({ history, onZoom }) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const onZoomRef = useRef(onZoom)
  onZoomRef.current = onZoom
  const theme = useTheme()

  const cum = useMemo(() => cumulativeSeries(history), [history])

  useEffect(() => {
    const chart = echarts.init(containerRef.current)
    chartRef.current = chart
    chart.group = CHART_GROUP
    echarts.connect(CHART_GROUP)

    // Drag-select zoom, shared with the online chart: the selection
    // goes upward and the parent refetches both from the same window.
    chart.on('datazoom', (event) => {
      const zoom = event.batch?.[0] || event
      if (zoom.startValue == null || zoom.endValue == null) return
      onZoomRef.current?.(zoom.startValue / 1000, zoom.endValue / 1000)
      // The select gesture also zoomed this chart locally, and that
      // window (absolute timestamps) would keep capping the axis after
      // the range picker moves on. Undo it immediately — the visible
      // zoom comes from the refetch with new axis bounds. (The reset
      // dispatch re-fires this handler percent-based, so the startValue
      // guard above stops recursion.)
      chart.dispatchAction({ type: 'dataZoom', start: 0, end: 100 })
    })

    // Same focus semantics as the online chart: click isolates a group,
    // clicking it again restores everyone.
    chart.on('legendselectchanged', ({ name, selected }) => {
      const names = Object.keys(selected)
      const before = { ...selected, [name]: !selected[name] }
      const wasFocused =
        before[name] && names.every((n) => n === name || !before[n])
      const target = {}
      names.forEach((n) => {
        target[n] = wasFocused ? true : n === name
      })
      chart.setOption({ legend: { selected: target } })
      // setOption drops the armed drag-select cursor — re-arm it.
      chart.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        dataZoomSelectActive: true,
      })
    })

    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(containerRef.current)
    return () => {
      observer.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return

    const textColor = theme.palette.text.secondary
    const gridColor = alpha(textColor, 0.15)

    chart.setOption(
      {
        animationDuration: 700,
        animationDurationUpdate: 400,
        legend: {
          top: 0,
          icon: 'circle',
          itemWidth: 10,
          itemHeight: 10,
          itemGap: 16,
          textStyle: { color: theme.palette.text.primary },
          inactiveColor: alpha(textColor, 0.3),
          data: PERSONAS.map(({ key }) => key),
        },
        grid: { left: 48, right: 16, top: 36, bottom: 28 },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line' },
          backgroundColor: 'rgba(33, 33, 41, 0.94)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 12 },
          formatter: tooltipFormatter,
          // Pin the tooltip to the crosshair's x (the snapped data
          // point), not the mouse — past the last point the crosshair
          // stops, and the tooltip stays with it.
          position: (point, params, dom, rect, size) => {
            const items = Array.isArray(params) ? params : [params]
            const x = chart.convertToPixel(
              { xAxisIndex: 0 },
              items[0].axisValue
            )
            const [w, h] = size.contentSize
            const left = x + 14 + w > size.viewSize[0] ? x - 14 - w : x + 14
            const top = Math.min(
              Math.max(point[1] - h / 2, 8),
              size.viewSize[1] - h - 8
            )
            return [left, top]
          },
        },
        // The toolbox must actually render for its dataZoom feature (the
        // drag-select machinery) to exist — `show: false` would skip
        // creating it entirely. Blank icon paths keep it invisible.
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: 'none',
              icon: { zoom: 'path://', back: 'path://' },
            },
          },
        },
        // No inside dataZoom: it would capture wheel events (blocking
        // page scroll) and isn't needed — the toolbox drag-select
        // dispatches its window upward and zooming happens by refetch.
        xAxis: {
          type: 'time',
          min: history.start * 1000,
          max: history.end * 1000,
          axisLabel: { color: textColor },
          axisLine: { lineStyle: { color: gridColor } },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'value',
          min: 0,
          axisLabel: { color: textColor, formatter: '{value} h' },
          splitLine: { lineStyle: { color: gridColor } },
        },
        series: cum.map(({ key, color, points }) => ({
          name: key,
          type: 'line',
          stack: 'mic-time',
          data: points,
          color,
          showSymbol: false,
          lineStyle: { width: 1 },
          areaStyle: { opacity: 0.5 },
          emphasis: { focus: 'series' },
        })),
      }
      // Merged (not replaced): the four persona series are fixed in name
      // and order, so polls extend the curve at the live edge instead of
      // re-entering the whole area.
    )

    chart.dispatchAction({
      type: 'takeGlobalCursor',
      key: 'dataZoomSelect',
      dataZoomSelectActive: true,
    })
  }, [cum, history, theme])

  return <Box ref={containerRef} sx={{ width: '100%', height: HEIGHT }} />
}

export default CumulativeTimeline
